import { initHelpFeedback } from './help-feedback.mount';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ConfigProvider, theme as antdTheme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import 'antd/dist/reset.css';
import './styles/theme.css';
import App from './App';
import { StandaloneEditor } from './fullscreen/StandaloneEditor';
import { StorageProbe } from './dev/StorageProbe';

const clayTheme = {
  algorithm: antdTheme.defaultAlgorithm,
  token: {
    colorPrimary: '#bc5a32',
    colorInfo: '#bc5a32',
    colorSuccess: '#1f5a2e',
    colorWarning: '#7a5210',
    colorError: '#a8362a',
    colorBgBase: '#f7f3eb',
    colorBgLayout: '#e6dfd2',
    colorBgContainer: '#f7f3eb',
    colorBgElevated: '#f7f3eb',
    colorText: '#2a2519',
    colorTextSecondary: '#615847',
    colorTextTertiary: '#998d75',
    colorTextQuaternary: '#c4b89e',
    colorBorder: 'rgba(50,30,0,.20)',
    colorBorderSecondary: 'rgba(50,30,0,.10)',
    colorFill: 'rgba(50,30,0,.06)',
    colorFillSecondary: 'rgba(50,30,0,.04)',
    colorFillTertiary: 'rgba(50,30,0,.03)',
    colorFillQuaternary: 'rgba(50,30,0,.02)',
    borderRadius: 8,
    borderRadiusLG: 10,
    borderRadiusSM: 6,
    fontFamily:
      '"Inter Tight", "Noto Sans SC", system-ui, -apple-system, "Helvetica Neue", sans-serif',
    fontSize: 13,
    fontSizeSM: 12,
    fontSizeLG: 14,
    controlHeight: 32,
    controlHeightSM: 26,
    controlHeightLG: 38,
    wireframe: false,
  },
  components: {
    Button: {
      defaultBg: 'transparent',
      defaultBorderColor: 'rgba(50,30,0,.20)',
      defaultHoverBg: 'rgba(50,30,0,.04)',
      defaultHoverBorderColor: '#2a2519',
      defaultHoverColor: '#2a2519',
      primaryShadow: 'none',
    },
    Card: {
      colorBgContainer: '#f7f3eb',
      headerBg: 'transparent',
      headerFontSize: 11,
      headerFontSizeSM: 11,
      paddingLG: 14,
    },
    Segmented: {
      itemSelectedBg: '#f7f3eb',
      itemSelectedColor: '#2a2519',
      itemHoverBg: 'rgba(50,30,0,.04)',
      itemColor: '#615847',
      trackBg: '#ede5d4',
      trackPadding: 2,
    },
    Tag: {
      defaultBg: '#ede5d4',
      defaultColor: '#615847',
    },
    Select: {
      colorBgContainer: '#f7f3eb',
      colorBorder: 'rgba(50,30,0,.20)',
    },
    Input: {
      colorBgContainer: '#f7f3eb',
      colorBorder: 'rgba(50,30,0,.20)',
    },
    InputNumber: {
      colorBgContainer: '#f7f3eb',
    },
    Alert: {
      colorInfoBg: 'rgba(188,90,50,.10)',
      colorInfoBorder: 'rgba(188,90,50,.30)',
    },
    Modal: {
      contentBg: '#f7f3eb',
      headerBg: '#f7f3eb',
    },
    Notification: {
      colorBgElevated: '#2a2519',
      colorText: '#f7f3eb',
    },
    Switch: {
      colorPrimary: '#bc5a32',
    },
    Divider: {
      colorSplit: 'rgba(50,30,0,.10)',
    },
  },
};

// 带 ?fs=1 的是「全屏编辑」开出来的独立标签页：那里没有多维表格 SDK，
// 走另一套数据来源（模板拉云同步、字段和记录读侧边栏存的快照），
// 所以整棵树都不一样，在入口就分流。
const isStandaloneEditor = new URLSearchParams(window.location.search).get('fs') === '1';
// ?probe=1：存储探针（在多维表格里以自定义插件方式打开），测 bridge.setData 能不能当共享存储
const isProbe = new URLSearchParams(window.location.search).get('probe') === '1';

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ConfigProvider locale={zhCN} theme={clayTheme} button={{ autoInsertSpace: false }}>
      {isProbe ? <StorageProbe /> : isStandaloneEditor ? <StandaloneEditor /> : <App />}
    </ConfigProvider>
  </StrictMode>,
);

// 帮助与反馈依赖 SDK，独立编辑器页里不初始化
if (!isStandaloneEditor) initHelpFeedback();
